import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';

// Queries
import postGlobal from '../Queries/postGlobal';
import getGlobal from '../Queries/getGlobal';

import ImageZoom from '../ImageZoom';

// Styles
import styles from './index.module.sass';

export default function ProductDetail({
  device,
  getCookie,
  setSessionMessage,
  catSelected,
  setAddedArray,
  setOrderData,
}) {
  const { t } = useTranslation('productdetail');
  const [detail, setDetail] = useState({});
  const [selectedTab, setSelectedTab] = useState('');
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [loading, setLoading] = useState(false);
  const [productQuantity, setProductQuantity] = useState(1);
  const { productcode = '' } = useParams();
  const { screenSize } = device;

  const getDetail = useCallback(async () => {
    setLoadingProduct(true);
    const resp = await getGlobal({
      tmbservice: 'GetFichaTecnicaProducto',
      jsonBody: {
        idProducto: productcode,
        idioma: window?.localStorage?.getItem('i18nextLng')?.includes('es') ? 0 : 1,
      },
    });

    const { listaCaracteristicas = [], listaIngredientes = [] } = resp;

    if (listaCaracteristicas.length > 0) setSelectedTab('features');
    else if (listaIngredientes.length > 0) setSelectedTab('ingredients');

    setDetail(resp);
    setLoadingProduct(false);
  }, [productcode]);

  async function addToCart(productId) {
    setLoading(true);
    const order = await postGlobal({
      tmbservice: 'getAgregarAlCarrito',
      jsonBody: {
        accion: 'ORDENAR',
        usuarioOK: getCookie('usuarioOk'),
        folioPedido: getCookie('folioPedido'),
        almacen: getCookie('warehouse'),
        categoria: 0,
        caller: 'consultarCart',
        clave: productId,
        cantidad: productQuantity,
        numeroLinea: '',
        textoProdSeleccion: '',
        idioma: window?.localStorage?.getItem('i18nextLng')?.includes('es') ? 0 : 1,
        origen: 'CARRITONUE',
      },
    });

    const { tipoMensaje = 'error', mensaje = '' } = order;

    setSessionMessage(mensaje);

    if (tipoMensaje === 'exito') {
      setAddedArray((oldArray) => {
        return [...oldArray, productId];
      });
      setOrderData(order);
    }

    setLoading(false);
  }
  function onChangeQuantity(action) {
    if (action === 'add') setProductQuantity(parseInt(productQuantity) + 1);
    else {
      if (parseInt(productQuantity) <= 1) setProductQuantity(1);
      else setProductQuantity(parseInt(productQuantity) - 1);
    }
  }

  function handleChange(e) {
    const value = e.target.value;
    setProductQuantity(value);
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    getDetail();
  }, [getDetail]);

  const { producto = {}, listaCaracteristicas = [], listaIngredientes = [] } = detail || {};

  return (
    <div className={`${styles.container} ${styles[screenSize]}`}>
      <div className={styles.orderHeader}>
        <div className={styles.return} onClick={() => window.history.back()}>
          <i className='material-icons-outlined'>navigate_before</i>
          <span className={styles.returnText}>{t('return')}</span>
        </div>
      </div>
      {(loadingProduct && (
        <div className={styles.productContainer}>
          <Skeleton variant='rectangular' animation='wave' className={styles.imageLoading} />
          <div className={styles.productData}>
            <Skeleton variant='rectangular' animation='wave' className={styles.loadingCode} />
            <Skeleton variant='rectangular' animation='wave' className={styles.loadingName} />
            <Skeleton variant='rectangular' animation='wave' className={styles.loadingCode} />
          </div>
        </div>
      )) || (
        <div className={styles.productContainer}>
          <div className={styles.productImage}>
            <div className={styles.imageContainer}>
              <ImageZoom
                device={device}
                imgsrc={detail?.producto?.imagenAlt || ''}
                idComplement={detail?.producto?.codigoProducto || ''}
                imageSize={400}
              />
            </div>
          </div>

          <div className={styles.productData}>
            <div className={styles.productId}>
              {t('productID')}: {producto?.codigoProducto || ''}
            </div>
            <div className={styles.productName}>
              {window?.localStorage?.getItem('i18nextLng')?.includes('es')
                ? producto?.nombreProducto1
                : producto?.nombreProducto2 || ''}
            </div>
            <div className={styles.productPrice}>{'$16.00'}</div>
            <div className={styles.actionsContainer}>
              <div className={`${styles.row} ${styles.quantity}`}>
                <i
                  className={`material-icons ${styles.quantityIcon}`}
                  onClick={() => onChangeQuantity('subtract')}>
                  remove
                </i>
                <input
                  type='text'
                  className={styles.quantityInput}
                  value={productQuantity || ''}
                  onChange={handleChange}
                />
                <i
                  className={`material-icons ${styles.quantityIcon}`}
                  onClick={() => onChangeQuantity('add')}>
                  add
                </i>
              </div>
              <div
                className={`${styles.row} ${styles.addToCart}`}
                onClick={() => {
                  if (!loading) {
                    addToCart(producto?.codigoProducto);
                  }
                }}>
                {(loading && <CircularProgress className={styles.circularProgress} />) || (
                  <>
                    <i className={`material-icons-outlined ${styles.bagIcon}`}>shopping_bag</i>
                    <span className={styles.buttonText}>{t('addText')}</span>
                  </>
                )}
              </div>
            </div>

            <div className={styles.productDescription}>
              <div className={styles.descTitle}>{t('descTitle')}</div>
              <div className={styles.descText}>
                {window?.localStorage?.getItem('i18nextLng')?.includes('es')
                  ? producto?.descripcion1
                  : producto?.descripcion2 || ''}
              </div>
            </div>
            <div className={styles.tabsContainer}>
              <div className={styles.tabsList}>
                {listaCaracteristicas.length > 0 && (
                  <div
                    className={`${styles.tab} ${styles[selectedTab === 'features']}`}
                    onClick={() => setSelectedTab('features')}>
                    {t('features')}
                  </div>
                )}
                {listaIngredientes.length > 0 && (
                  <div
                    className={`${styles.tab} ${styles[selectedTab === 'ingredients']}`}
                    onClick={() => setSelectedTab('ingredients')}>
                    {t('ingredients')}
                  </div>
                )}
              </div>
              <div>
                {selectedTab === 'features' && listaCaracteristicas.length > 0 && (
                  <ul className={styles.featuresContainer}>
                    {listaCaracteristicas.map((feature, key) => {
                      const { descripcion1 = '', descripcion2 = '' } = feature;
                      return (
                        <li key={key}>
                          {window?.localStorage?.getItem('i18nextLng')?.includes('es')
                            ? descripcion1
                            : descripcion2 || ''}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {selectedTab === 'ingredients' && listaIngredientes.length > 0 && (
                  <div>
                    {listaIngredientes.map((ingredient, key) => {
                      const {
                        descripcion1 = '',
                        descripcion2 = '',
                        nombre1 = '',
                        nombre2 = '',
                      } = ingredient;
                      return (
                        <div key={key}>
                          <div className={styles.ingTitle}>
                            {window?.localStorage?.getItem('i18nextLng')?.includes('es')
                              ? nombre1
                              : nombre2 || ''}
                          </div>
                          <div className={styles.ingText}>
                            {window?.localStorage?.getItem('i18nextLng')?.includes('es')
                              ? descripcion1
                              : descripcion2 || ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
